import { QueryClient } from '@tanstack/react-query';

// Enhanced session storage for persistence
const sessionStoragePersister = {
  getItem: (key: string) => {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      console.warn('Failed to read from sessionStorage:', error);
      return null;
    }
  },
  setItem: (key: string, value: string) => {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      console.warn('Failed to write to sessionStorage:', error);
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        try {
          sessionStorage.clear();
          sessionStorage.setItem(key, value);
        } catch (retryError) {
          console.error('Failed to clear and retry sessionStorage:', retryError);
        }
      }
    }
  },
  removeItem: (key: string) => {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to remove from sessionStorage:', error);
    }
  },
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
      retry: (failureCount, error) => {
        // Don't retry on authentication errors
        const errorWithStatus = error as any;
        if (errorWithStatus?.code === 'PGRST116' || errorWithStatus?.status === 401 || errorWithStatus?.status === 403) {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: (failureCount, error) => {
        // Don't retry on authentication or validation errors
        const errorWithStatus = error as any;
        if (errorWithStatus?.code === 'PGRST116' || errorWithStatus?.status === 401 || errorWithStatus?.status === 403 || errorWithStatus?.status === 422) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

// Initialize manual persistence for list queries
const persistQueryData = () => {
  const queryCache = queryClient.getQueryCache();
  
  // Persist list queries to session storage
  const persistableQueries = ['clients', 'proposals', 'invoices', 'projects', 'activities', 'suppliers'];
  
  queryCache.getAll().forEach(query => {
    const queryKey = query.queryKey[0] as string;
    if (persistableQueries.some(key => queryKey?.includes?.(key) && !queryKey?.includes?.('detail'))) {
      try {
        sessionStoragePersister.setItem(
          `query_${JSON.stringify(query.queryKey)}`,
          JSON.stringify({
            data: query.state.data,
            dataUpdatedAt: query.state.dataUpdatedAt,
            timestamp: Date.now()
          })
        );
      } catch (error) {
        console.warn('Failed to persist query data:', error);
      }
    }
  });
};

// Restore persisted queries on app load
const restorePersistedQueries = () => {
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith('query_')) {
        const queryKey = JSON.parse(key.replace('query_', ''));
        const stored = sessionStorage.getItem(key);
        
        if (stored) {
          const parsed = JSON.parse(stored);
          // Only restore if data is less than 1 hour old
          if (Date.now() - parsed.timestamp < 60 * 60 * 1000) {
            queryClient.setQueryData(queryKey, parsed.data);
          } else {
            sessionStorage.removeItem(key);
          }
        }
      }
    }
  } catch (error) {
    console.warn('Failed to restore persisted queries:', error);
  }
};

// Set up persistence hooks
queryClient.getQueryCache().subscribe(() => {
  // Debounce persistence
  clearTimeout((globalThis as any).__persistTimeout);
  (globalThis as any).__persistTimeout = setTimeout(persistQueryData, 1000);
});

// Restore on initialization
restorePersistedQueries();